import React from 'react';
import {
  SimpleGrid,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Stack,
  Text as TextComponent,
  Box,
  Tag,
  Heading,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';
import { FormInputs } from '../types/formInputs';
import {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  Control,
  Controller,
  UseFormWatch,
} from 'react-hook-form';
import moment from 'moment';
import {
  DatePicker,
  Select,
  Input,
  Textarea,
  RadioGroup,
} from '@landsvirkjun/react.common';
import { IconInfo } from 'components/Icons';
import { countryOptions, timeOptions } from '../data/options';

type TravelInformationSectionProps = {
  errors: FieldErrors<FormInputs>;
  register: UseFormRegister<FormInputs>;
  setValue: UseFormSetValue<FormInputs>;
  control: Control<FormInputs, any>;
  watch: UseFormWatch<FormInputs>;
};

export const TravelInformationSection = ({
  errors,
  register,
  setValue,
  control,
  watch,
}: TravelInformationSectionProps): JSX.Element => {
  const country = watch('country');
  const departureTime = watch('departureTime');
  const departureDate = watch('departureDate');
  const arrivalTime = watch('arrivalTime');
  const arrivalDate = watch('arrivalDate');
  const radioExtraDays = watch('radioExtraDays');
  const arrangeFlight = watch('arrangeFlight');
  const isFlightBooking = arrangeFlight === 'true';

  return (
    <Box as="div">
      <Stack direction="row" pb={10}>
        <Tag borderRadius="full">2</Tag>
        <Heading as="h3" size="md">
          Ferðaupplýsingar
        </Heading>
      </Stack>
      <SimpleGrid spacing={10} columns={{ base: 1, md: 2 }}>
        <SimpleGrid spacing={10} columns={{ base: 1 }}>
          <FormControl isInvalid={!!errors.country}>
            <FormLabel>Veldu land sem ferðast er til</FormLabel>
            <Controller
              control={control}
              name="country"
              render={({ field: { onChange } }): JSX.Element => (
                <Select
                  onChange={onChange}
                  placeholder="Veldu land úr lista"
                  defaultValue={
                    country
                      ? {
                          label: country,
                          value: country,
                        }
                      : null
                  }
                  options={countryOptions.sort((a, b) =>
                    a.label.localeCompare(b.label)
                  )}
                  error={!!errors.country}
                />
              )}
            />
            <FormErrorMessage>{errors.country?.message}</FormErrorMessage>
          </FormControl>
          {country === 'Annað' && (
            <FormControl isInvalid={!!errors.customCountry}>
              <FormLabel>Sláðu inn nafn á landi</FormLabel>
              <Input {...register('customCountry')} />
              <FormErrorMessage>
                {errors.customCountry?.message}
              </FormErrorMessage>
            </FormControl>
          )}
        </SimpleGrid>
        <FormControl isInvalid={!!errors.city}>
          <FormLabel>Borg</FormLabel>
          <Input placeholder="Sláðu inn heiti á borg" {...register('city')} />
          <FormErrorMessage>{errors.city?.message}</FormErrorMessage>
        </FormControl>
        <SimpleGrid spacing={10} columns={{ base: 1, md: 2 }}>
          <FormControl isInvalid={!!errors.departureDate}>
            <FormLabel>Brottför</FormLabel>
            <Controller
              control={control}
              name="departureDate"
              render={({ field: { name } }): JSX.Element => (
                <DatePicker
                  name={name}
                  date={departureDate ? moment.utc(departureDate) : null}
                  id="datepicker"
                  placeholder="Veldu dagsetningu"
                  onChange={(value): void => {
                    setValue('departureDate', moment(value).format());
                  }}
                  error={!!errors.departureDate}
                />
              )}
            />
            <FormErrorMessage>{errors.departureDate?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors.departureTime}>
            <FormLabel>Tími</FormLabel>
            <Controller
              control={control}
              name="departureTime"
              render={({ field: { onChange } }): JSX.Element => (
                <Select
                  placeholder="Veldu tíma"
                  options={timeOptions}
                  onChange={onChange}
                  defaultValue={
                    departureTime
                      ? {
                          label: departureTime,
                          value: departureTime,
                        }
                      : null
                  }
                  error={!!errors.departureTime}
                />
              )}
            />
            <FormErrorMessage>{errors.departureTime?.message}</FormErrorMessage>
          </FormControl>
        </SimpleGrid>
        <SimpleGrid spacing={10} columns={{ base: 1, md: 2 }}>
          <FormControl isInvalid={!!errors.arrivalDate}>
            <FormLabel>Heimkoma</FormLabel>
            <Controller
              control={control}
              name="arrivalDate"
              render={({ field: { name } }): JSX.Element => (
                <DatePicker
                  name={name}
                  disabled={departureDate === ''}
                  onlySpecificRange
                  visibleMonthOpen={(): moment.Moment => moment(departureDate)}
                  startDate={moment(departureDate)}
                  endDate={moment().add(1, 'years')}
                  date={arrivalDate ? moment.utc(arrivalDate) : null}
                  id="datepicker"
                  placeholder="Veldu dagsetningu"
                  onChange={(value): void => {
                    setValue('arrivalDate', moment(value).format());
                  }}
                  error={!!errors.arrivalDate}
                />
              )}
            />
            <FormErrorMessage>{errors.arrivalDate?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors.arrivalTime}>
            <FormLabel>Tími</FormLabel>
            <Controller
              control={control}
              name="arrivalTime"
              render={({ field: { onChange } }): JSX.Element => (
                <Select
                  placeholder="Veldu tíma"
                  onChange={onChange}
                  options={timeOptions}
                  defaultValue={
                    arrivalTime
                      ? {
                          label: arrivalTime,
                          value: arrivalTime,
                        }
                      : null
                  }
                  error={!!errors.arrivalTime}
                />
              )}
            />
            <FormErrorMessage>{errors.arrivalTime?.message}</FormErrorMessage>
          </FormControl>
        </SimpleGrid>
      </SimpleGrid>

      <Stack direction="column" mt={10} pb={4}>
        <FormLabel fontWeight={700}>Flugbókun</FormLabel>
        <FormControl>
          <RadioGroup
            mb={5}
            onChange={(val): void => setValue('arrangeFlight', val)}
            value={arrangeFlight}
            buttonValues={[
              {
                text: 'Ég vil láta bóka flug fyrir mig',
                value: 'true',
              },
              {
                text: 'Ég mun sjá um að bóka flugið',
                value: 'false',
              },
            ]}
          />
        </FormControl>
      </Stack>

      <SimpleGrid spacing={10} mb={10} columns={{ base: 1, md: 2 }}>
        <FormControl isDisabled={!isFlightBooking}>
          <FormLabel>Flugbókun, farangur og ferðatilhögun</FormLabel>
          <Textarea
            isDisabled={!isFlightBooking}
            placeholder="Skráðu farangursóskir og ferðatilhögun t.d. ef fleiri eru að ferðast saman"
            {...register('baggageRequirements')}
          />
        </FormControl>
        {isFlightBooking && (
          <FormControl>
            <Stack direction="row" alignItems="center" mb={2}>
              <IconInfo style={{ color: '#0092ce' }} />
              <FormLabel fontWeight={700}>
                Innritaður farangur og séróskir vegna flugbókunar
              </FormLabel>
            </Stack>
            <TextComponent color="gray.500">
              Vinsamlegast takið fram ef þið eruð með innritaðan farangur, hvort
              fleiri munu ferðast saman, ásamt öðrum séróskum varðandi bókunina.
              <UnorderedList>
                <ListItem>Fjöldi innritaðra taska</ListItem>
                <ListItem>Töskur yfir 20 kg.</ListItem>
              </UnorderedList>
            </TextComponent>
          </FormControl>
        )}
        {!isFlightBooking && (
          <FormControl>
            <Stack direction="row" alignItems="center" mb={2}>
              <IconInfo style={{ color: '#0092ce' }} />
              <FormLabel fontWeight={700}>
                Starfsmaður sér um að bóka flug og farangur
              </FormLabel>
            </Stack>
            <TextComponent color="gray.500">
              Starfsmaður sér um allar séróskir er varða flugið og skráir inn
              kvittanir. Athuga þarf að nafn Landsvirkjunar komi fram á
              reikningi ef mögulegt er.
            </TextComponent>
          </FormControl>
        )}
      </SimpleGrid>

      <SimpleGrid spacing={10} columns={{ base: 1, md: 2 }}>
        <FormControl>
          <FormLabel>Aðrar bókanir og séróskir</FormLabel>
          <Textarea
            placeholder="t.d. hvort bóka skuli bíl eða aðrar séróskir"
            {...register('travelArrangements')}
          />
        </FormControl>
        <FormControl>
          <Stack direction="row" alignItems="center" mb={2}>
            <IconInfo style={{ color: '#0092ce' }} />
            <FormLabel fontWeight={700}>Aðrar bókanir og séróskir</FormLabel>
          </Stack>
          <TextComponent color="gray.500">
            Hér þurfa að koma fram skilaboð hvort bóka skuli bíl eða aðrar
            séróskir tengdar ferðinni.
          </TextComponent>
        </FormControl>
        <Stack direction="column">
          <RadioGroup
            mb={5}
            value={radioExtraDays}
            onChange={(val: string): void => {
              setValue('radioExtraDays', val);
              setValue('addExtraDays', null);
            }}
            buttonValues={[
              {
                text: 'Öll ferðin á vegum Landsvirkjunar',
                value: 'false',
              },
              {
                text: 'Ég vil bæta við dögum á eigin vegum',
                value: 'true',
              },
            ]}
          />
          {radioExtraDays === 'true' && (
            <FormControl isInvalid={!!errors.addExtraDays}>
              <FormLabel>Fjöldi daga</FormLabel>
              <Input type="number" {...register('addExtraDays')} />
              <FormErrorMessage>
                {errors.addExtraDays?.message}
              </FormErrorMessage>
            </FormControl>
          )}
        </Stack>
      </SimpleGrid>
      <SimpleGrid spacing={10} columns={1} mt={10}>
        <FormControl>
          <FormLabel fontWeight={700} mb={5}>
            Hótelgisting
          </FormLabel>
          <RadioGroup
            onChange={(val): void => setValue('arrangeAccommodation', val)}
            value={watch('arrangeAccommodation')}
            buttonValues={[
              {
                text: 'Ég vil láta bóka hótel fyrir mig',
                value: 'true',
              },
              {
                text: 'Ég mun sjá um að bóka hótel',
                value: 'false',
              },
            ]}
          />
        </FormControl>
        <SimpleGrid spacing={10} columns={{ base: 1, md: 2 }}>
          <FormControl>
            <FormLabel>Hóteltillaga</FormLabel>
            <Textarea
              placeholder="Skráðu inn tillögu að hóteli, ásamt heimilisfangi og símanúmer hótelsins vegna SOS international"
              {...register('accommodationDetails')}
            />
          </FormControl>
          <FormControl>
            <Stack direction="row" alignItems="center" mb={2}>
              <IconInfo style={{ color: '#0092ce' }} />
              <FormLabel fontWeight={700}>Áríðandi vegna hótelbókana</FormLabel>
            </Stack>
            <TextComponent color="gray.500">
              Ef þú bókar hótel sjálf/ur er mikilvægt að á reikningi í bókun
              komi fram nafn Landsvirkjunar (vegna endurgreiðslu á vsk.
              erlendis.) Vegna SOS International er mikilvægt að setja inn
              heimilisfang og símanúmer hótels.
            </TextComponent>
          </FormControl>
        </SimpleGrid>
      </SimpleGrid>
    </Box>
  );
};
